import { ApolloClient, InMemoryCache, gql } from '@apollo/client';

import * as ApolloTypes from '../graphqlTypes/types';
import { localStorageWithMemoryFallback } from '../localStorageWithMemoryFallback';

interface Context {
  client: ApolloClient<InMemoryCache>;
}

export const typeDefs = gql`
  extend type Query {
    isLoggedIn: Boolean
    currentAccountId: ID
  }

  extend type Account {
    planUpgradeOptionsMock: [OnboardingPlanOption!]
  }

  extend type Mutation {
    # Save the current org id. Pass null to delete the saved value.
    setCurrentAccountId(currentAccountId: ID): ID
  }
`;

export const resolvers = {
  Query: {
    currentAccountId() {
      return JSON.parse(
        localStorageWithMemoryFallback.getItem('engine:currentAccountId') ||
          'null',
      );
    },
  },
  Account: {
    planUpgradeOptionsMock: () => {
      return [
        {
          __typename: 'OnboardingPlanOption',
          planReadableId: 'free',
          canTransitionTo: false,
          name: 'Free',
          description: 'Learn and experiment with the latest GraphQL tools.',
          pricePerUnitDescription: '$0 forever',
          priceSimpleDescription: 'Upgrade at any time.',
          promoCreditsInCents: 0,
          totalDueOnSignupInCents: 0,
          billingCycleAnchor: ApolloTypes.BillingCycleAnchor.START_OF_MONTH,
          isSelfService: true,
          includedFeatures: [
            'Limited to 3 developers',
            'Self-hosted GraphOS Router limited to 60 requests/min',
            '1 day of data retention for observability and operation checks',
          ],
          excludedFeatures: ['Not recommended for production use cases'],
        },
        {
          __typename: 'OnboardingPlanOption',
          planReadableId: 'scale-developer-stripe',
          canTransitionTo: false,
          name: 'Developer',
          description: 'For small teams operating a production GraphQL API.',
          pricePerUnitDescription: 'Starting at $5 / million requests',
          priceSimpleDescription: 'No subscription fee. Usage-based pricing.',
          promoCreditsInCents: 0,
          totalDueOnSignupInCents: 0,
          billingCycleAnchor: ApolloTypes.BillingCycleAnchor.START_OF_MONTH,
          isSelfService: true,
          includedFeatures: [
            'Up to 10 developers',
            '7 days data retention',
            'GraphOS – Apollo’s complete API management platform',
            'Supports any GraphQL API',
            'Self-hosted GraphOS Router',
            'Built-in support for GraphQL federation, Connectors, Entity Caching, federated Subscriptions, and more',
          ],
          excludedFeatures: [
            'Schema Proposals - collaboration & approval workflows for schema development',
            'Single sign-on (SSO)',
            'Audit logging',
            'Custom MSA',
          ],
          ineligibleReasons: [
            {
              __typename: 'TooManyUsers',
              severity: ApolloTypes.PlanIneligibilityReasonSeverity.ERROR,
              currentUsers: 20,
              maxUsers: 10,
            },
            {
              __typename: 'SsoEnabled',
              severity: ApolloTypes.PlanIneligibilityReasonSeverity.ERROR,
            },
          ],
        },
        {
          __typename: 'OnboardingPlanOption',
          planReadableId: 'scale-standard-stripe',
          canTransitionTo: true,
          name: 'Standard',
          description:
            'For scaling a GraphQL platform across multiple teams and services.',
          pricePerUnitDescription: 'Starting at $8 per million requests',
          priceSimpleDescription: '$2,500 / month, billed annually.',
          promoCreditsInCents: 0,
          totalDueOnSignupInCents: 0,
          billingCycleAnchor: ApolloTypes.BillingCycleAnchor.START_OF_MONTH,
          isSelfService: false,
          includedFeatures: [
            'Up to 30 developers',
            '120 days data retention',
            'GraphOS – Apollo’s complete API management platform',
            'Supports any GraphQL API',
            'Self-hosted GraphOS Router',
            'Built-in support for GraphQL federation, Connectors, Entity Caching, federated Subscriptions, and more',
            'Schema Proposals - collaboration & approval workflows for schema development',
            'Single sign-on (SSO)',
          ],
          excludedFeatures: ['Audit logging', 'Custom MSA'],
        },
      ];
    },
  },
  Mutation: {
    setCurrentAccountId: (
      _root: unknown,
      {
        currentAccountId = null,
      }: ApolloTypes.SetCurrentAccountIdMutationVariables,
      context: Context,
    ) => {
      context.client.writeQuery<ApolloTypes.ClientSchemaSetCurrentAccountIdCacheWriteQuery>(
        {
          query: gql`
            query ClientSchemaSetCurrentAccountIdCacheWriteQuery {
              currentAccountId @client
            }
          `,
          data: { currentAccountId },
        },
      );

      if (currentAccountId) {
        localStorageWithMemoryFallback.setItem(
          'engine:currentAccountId',
          JSON.stringify(currentAccountId),
        );
      } else {
        localStorageWithMemoryFallback.removeItem('engine:currentAccountId');
      }

      return currentAccountId;
    },
  },
};
